import { useNavigate } from 'react-router-dom';

import { Badge, DataTable, Icon, type Column, type SortDirection } from '@/components/ui';
import {
  formatDurationClock,
  formatMode,
  formatSessionDateTimeShort,
} from '@/lib/formatting';
import {
  effectiveDurationSec,
  hasRawFile,
  sessionIdentifier,
  sessionTimestamp,
  titleFor,
  type SessionSummary,
} from '../model';

import styles from './SessionsTable.module.css';

export type SessionSortKey = 'date' | 'title' | 'duration' | 'score';

interface SessionsTableProps {
  sessions: readonly SessionSummary[];
  sort: { key: SessionSortKey; direction: SortDirection };
  onSortChange: (key: SessionSortKey) => void;
  locale: string;
  /** Renders the card list instead of the table. */
  compact?: boolean;
}

export function SessionsTable({
  sessions,
  sort,
  onSortChange,
  locale,
  compact = false,
}: SessionsTableProps) {
  const navigate = useNavigate();
  const open = (session: SessionSummary) => navigate(`/sessions/${session.id}`);

  if (compact) {
    return (
      <div className={styles.cards}>
        {sessions.map((session) => (
          <button
            key={session.id}
            type="button"
            className={styles.card}
            onClick={() => open(session)}
          >
            <div className={styles.cardBody}>
              <div className={styles.cardTitle}>{titleFor(session, locale)}</div>
              <div className={styles.cardMeta}>
                <span>{formatSessionDateTimeShort(sessionTimestamp(session))}</span>
                <span>{formatDurationClock(effectiveDurationSec(session))}</span>
                {session.valuesAvg !== null ? <span>avg {session.valuesAvg.toFixed(2)}</span> : null}
              </div>
              <div className={styles.cardTags}>
                {session.mode !== null ? <Badge>{formatMode(session.mode)}</Badge> : null}
                {hasRawFile(session) ? (
                  <Badge tone="signal" dot>
                    EEG
                  </Badge>
                ) : null}
              </div>
            </div>
            <Icon name="chevron-right" size={16} className={styles.chevron} />
          </button>
        ))}
      </div>
    );
  }

  const columns: Column<SessionSummary, SessionSortKey>[] = [
    {
      id: 'title',
      header: 'Session',
      sortKey: 'title',
      render: (session) => (
        <div className={styles.title}>
          <span className={styles.titleText}>{titleFor(session, locale)}</span>
        </div>
      ),
    },
    {
      id: 'mode',
      header: 'Mode',
      hideBelow: 'md',
      render: (session) =>
        session.mode !== null ? (
          <Badge>{formatMode(session.mode)}</Badge>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      id: 'date',
      header: 'Recorded',
      sortKey: 'date',
      render: (session) => formatSessionDateTimeShort(sessionTimestamp(session)),
    },
    {
      id: 'duration',
      header: 'Duration',
      sortKey: 'duration',
      align: 'right',
      render: (session) => formatDurationClock(effectiveDurationSec(session)),
    },
    {
      id: 'score',
      header: 'Avg',
      sortKey: 'score',
      align: 'right',
      hideBelow: 'sm',
      render: (session) =>
        session.valuesAvg !== null ? (
          <span className={styles.score}>{session.valuesAvg.toFixed(2)}</span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      id: 'peak',
      header: 'Peak',
      align: 'right',
      hideBelow: 'lg',
      render: (session) =>
        session.valuesMax !== null ? (
          <span className={styles.score}>{session.valuesMax.toFixed(2)}</span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      id: 'raw',
      header: 'Raw EEG',
      hideBelow: 'md',
      render: (session) =>
        hasRawFile(session) ? (
          <Badge tone="signal" dot>
            Available
          </Badge>
        ) : (
          <span className={styles.muted}>None</span>
        ),
    },
    {
      id: 'identifier',
      header: 'Identifier',
      hideBelow: 'lg',
      render: (session) => <code className={styles.identifier}>{sessionIdentifier(session)}</code>,
    },
    {
      id: 'open',
      header: '',
      width: '40px',
      render: () => <Icon name="chevron-right" size={15} className={styles.chevron} />,
    },
  ];

  return (
    <DataTable
      rows={sessions}
      columns={columns}
      rowKey={(session) => session.id}
      onRowClick={open}
      sort={sort}
      onSortChange={onSortChange}
      caption="Your recorded meditation sessions"
    />
  );
}

/** Sorting the already-loaded list, client-side. */
export function sortSessions(
  sessions: readonly SessionSummary[],
  key: SessionSortKey,
  direction: SortDirection,
  locale: string,
): SessionSummary[] {
  const factor = direction === 'asc' ? 1 : -1;

  return [...sessions].sort((a, b) => {
    switch (key) {
      case 'title':
        return factor * titleFor(a, locale).localeCompare(titleFor(b, locale));
      case 'duration':
        return factor * (nullLast(effectiveDurationSec(a)) - nullLast(effectiveDurationSec(b)));
      case 'score':
        return factor * (nullLast(a.valuesAvg) - nullLast(b.valuesAvg));
      case 'date':
      default: {
        const x = sessionTimestamp(a);
        const y = sessionTimestamp(b);
        // Undated rows always sink, whichever way the column is sorted.
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return factor * (x.getTime() - y.getTime());
      }
    }
  });
}

function nullLast(value: number | null): number {
  return value ?? Number.NEGATIVE_INFINITY;
}
